import { resolveApiBaseUrl } from '@tsgecart/config';
import type { ApiResponse, ApiErrorCode } from '@tsgecart/shared';
import { webEnv } from '../env.js';

/**
 * The customer-facing counterpart to `apps/web/src/features/admin/api/admin-client.ts` --
 * same shape, same envelope-unwrapping/error-sanitizing contract, just
 * pointed at customer routes (cart/wishlist/addresses/checkout/orders/
 * payments/rewards/notifications) instead of `/admin/*`. Every
 * storefront feature's API module goes through this one function, never
 * raw `fetch` -- mirrors the admin dashboard's own established
 * convention rather than inventing a second pattern.
 */
export const apiBaseUrl = resolveApiBaseUrl({
  environment: webEnv.VITE_APP_ENV,
  configuredUrl: webEnv.VITE_API_BASE_URL,
});

export type StorefrontApiErrorCode = ApiErrorCode | 'NETWORK_ERROR' | 'ABORTED' | 'PARSE_ERROR';

export class StorefrontApiError extends Error {
  constructor(
    public readonly code: StorefrontApiErrorCode,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'StorefrontApiError';
  }
}

/** Never surfaces a raw backend `error.message` for a 500 -- same defensive rule as the admin client's `safeErrorMessage`. */
function safeErrorMessage(code: ApiErrorCode, message: string): string {
  if (code === 'INTERNAL_SERVER_ERROR') {
    return 'Something went wrong. Please try again.';
  }
  return message;
}

export interface StorefrontApiFetchOptions {
  /** Optional -- omit for the handful of genuinely public routes (catalog reads, delivery serviceability). Every authenticated call passes the Clerk token obtained at query-time via `useAuth().getToken()`, never cached in module state or localStorage. */
  token?: string | null;
  signal?: AbortSignal;
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Extra request headers -- e.g. the guest cart's `X-Guest-Cart-Token`, sent as a header (never a cookie) so there is no ambient credential the browser attaches automatically. */
  headers?: Record<string, string>;
}

export async function storefrontApiFetch<T>(path: string, options: StorefrontApiFetchOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers: {
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (error) {
    if (options.signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
      throw new StorefrontApiError('ABORTED', 'Request was cancelled.', 0);
    }
    throw new StorefrontApiError('NETWORK_ERROR', 'Could not reach the server. Check your connection.', 0);
  }

  let body: ApiResponse<T>;
  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new StorefrontApiError('PARSE_ERROR', 'Received an unexpected response from the server.', response.status);
  }

  if (!body.success) {
    throw new StorefrontApiError(
      body.error.code,
      safeErrorMessage(body.error.code, body.error.message),
      response.status,
    );
  }

  return body.data;
}
